import { AuthResp, ChangePasswordPayload, LoginPayload, UIError } from 'common/interfaces';
import UILogger from 'common/utils/logger';
import { AuthActions } from 'reducers/auth-reducer';
import { postChangePassword, postForgotPassword, postLogin, postResetPassword } from 'services/auth-service';
import { initHtttpAuthHeader } from 'services/config';
import { persistUserAuthToken, persistsUserData } from 'services/db-service';
import { identifyAnalyticsUser } from 'services/analytics-service';
import { useAppDispatch } from 'setup/store';
import { useAppActions } from './app-actions';
import { useSessionActions } from './session-actions';
import { useTranslation } from 'react-i18next';
import { IUserProfile } from 'common/interfaces/iprofile';

export const useAuthContext = () => {
  const dispatch = useAppDispatch();
  const { setUserLoggedIn, setLoggedInUser, setAppInitialized } = useAppActions();
  const { setRoleAccessData, setSelectedBrand } = useSessionActions();
  const { t } = useTranslation();

  const setLoading = (isLoading: boolean) => {
    dispatch(AuthActions.setLoading(isLoading));
  };

  const setError = (error: UIError | null) => {
    dispatch(AuthActions.setError(error));
  };
  const setRequestOtpError = (error: UIError | null) => {
    dispatch(AuthActions.setRquestOtpError(error));
  };

  const login = async (payload: LoginPayload, rememberMe: boolean) => {
    UILogger.info('login request', payload);
    if (!payload.password) {
      UILogger.info('login request: Invalid password');
      setError({
        msg: t('error.invalidPwd'),
      } as UIError);
    } else if (!payload.username) {
      UILogger.info('login request: Invalid username');
      setError({
        msg: t('error.invalidUserName'),
      } as UIError);
    } else {
      setError(null);
      setLoading(true);
      const resp = await postLogin(payload);
      setLoading(false);
      if (resp.status.success) {
        UILogger.log('Loggin successful msg got from server', resp.data);
        if (resp.data && resp.data.user?.id) {
          await handlePostUserLogin(resp, rememberMe);
        }
      } else {
        setError({
          msg: resp.status.msg ? resp.status.msg : t('error.invalidUserNamePwd'),
        } as UIError);
      }
    }
  };

  const handlePostUserLogin = async (resp: AuthResp, rememberMe: boolean) => {
    const lnUser = resp.data.user;
    if (lnUser) {
      persistsUserData(lnUser, rememberMe ? 'localStorage' : 'sessionStorage');
      persistUserAuthToken(resp.data.token, rememberMe ? 'localStorage' : 'sessionStorage');
      if (resp.data.brands && resp.data.brands.length == 1) {
        UILogger.log('handlePostUserLogin: setting brand', resp.data.brands);
        setSelectedBrand(resp.data.brands[0]);
      }
      setUserLoggedIn(true);
      setRoleAccessData(resp.data.role);
      setAppInitialized(true);

      await initHtttpAuthHeader(resp.data.brands && resp.data.brands.length == 1 ? resp.data.brands[0].id : undefined);
      identifyAnalyticsUser(lnUser, resp.data.role?.roleName);
    }
    dispatch(AuthActions.setLoggedInUser(lnUser));
    setLoggedInUser(lnUser as IUserProfile);
  };

  const resetAuthError = async () => {
    setError(null);
    setRequestOtpError(null);
    setLoading(false);
  };

  const forgotPassword = async (username: string) => {
    UILogger.info('forgotPassword request', username);
    if (!username) {
      UILogger.info('forgotPassword request: Invalid username');
      setError({
        msg: t('error.invalidUserName'),
      } as UIError);
    } else {
      setError(null);
      setLoading(true);
      const resp = await postForgotPassword(username);
      setLoading(false);

      if (resp.status.success) {
        UILogger.log('forgotPassword successful msg got from server', resp);
        dispatch(AuthActions.setOtpRequested({ username } as LoginPayload));
        return true;
      } else {
        setError({
          msg: resp.status.msg ? resp.status.msg : t('error.invalidUserNamePwd'),
        } as UIError);
      }
    }
  };
  const resetPassword = async (loginPaylod: LoginPayload) => {
    UILogger.info('resetPassword request', loginPaylod);
    if (!loginPaylod.username) {
      UILogger.info('resetPassword request: Invalid username');
      setError({
        msg: t('error.invalidUserName'),
      } as UIError);
    }
    if (!loginPaylod.token) {
      UILogger.info('resetPassword request: Invalid token');
      setError({
        msg: t('error.invalidToken'),
      } as UIError);
    } else {
      setError(null);
      setLoading(true);
      const resp = await postResetPassword(loginPaylod.token, loginPaylod);
      setLoading(false);

      if (resp.status.success) {
        UILogger.log('resetPassword successful msg got from server', resp);
        dispatch(AuthActions.setPasswordResetDone(true));
        return true;
      } else {
        setError({
          msg: resp.status.msg ? resp.status.msg : t('error.invalidUserNamePwd'),
        } as UIError);
      }
    }
  };

  const changePassword = async (paylod: ChangePasswordPayload) => {
    UILogger.info('changePassword request');
    if (!paylod.currentPassword) {
      UILogger.info('changePassword request: Invalid CurrentPassword');
      setError({
        msg: t('error.invalidCurrentPassword'),
      } as UIError);
    }
    if (!paylod.password) {
      UILogger.info('changePassword request: Invalid New Password');
      setError({
        msg: t('error.invalidNewPassword'),
      } as UIError);
    } else {
      setError(null);
      setLoading(true);
      const resp = await postChangePassword(paylod.currentPassword, paylod.password);
      setLoading(false);

      if (resp.status.success) {
        UILogger.log('changePassword successful msg got from server', resp);
        dispatch(AuthActions.setPasswordResetDone(true));
        return true;
      } else {
        setError({
          msg: resp.status.msg ? resp.status.msg : t('error.unableToProcess'),
        } as UIError);
      }
    }
  };

  return {
    login,
    setError,
    resetAuthError,
    setLoading,
    forgotPassword,
    resetPassword,
    changePassword,
  };
};
